import Cookies from 'js-cookie';
import { googleLogout } from '@react-oauth/google';
import { API_BASE_URL } from '../config/apiBaseUrl';
import type { ApiError } from './models';

const baseURL = API_BASE_URL;

// Simple logout function that can be called from interceptors
const performLogout = () => {
  googleLogout();
  localStorage.clear();
  // Redirect to login page
  window.location.href = '/login';
};

/** True when `data` is the API's standard error body (see ApiError). */
export const isApiErrorBody = (data: unknown): data is ApiError =>
  typeof data === 'object' &&
  data !== null &&
  typeof (data as ApiError).detail === 'string' &&
  typeof (data as ApiError).code === 'string';

// What every failed request throws. `data` is the response body; for API
// errors it's an ApiError, and `message` is its `detail`, ready to show.
export class FetchError<TData = unknown> extends Error {
  status: number;
  data: TData;

  constructor(message: string, status: number, data: TData) {
    super(message);
    this.name = 'FetchError';
    this.status = status;
    this.data = data;
  }
}

// Orval types each hook's error as ErrorType<the schema's error body>.
export type ErrorType<Error> = FetchError<Error>;

// Type definitions matching axios structure for orval compatibility
export type RequestConfig = {
  url?: string;
  method?: string;
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

export type CancellablePromise<T> = Promise<T> & {
  cancel: () => void;
};

// Custom instance function for orval (axios-like signature, fetch implementation)
// This returns unwrapped data just like axios did
export const customInstance = <T>(
  config: RequestConfig,
  options?: RequestConfig
): CancellablePromise<T> => {
  const controller = new AbortController();

  // Merge config and options
  const mergedConfig = { ...config, ...options };
  const { url = '', method = 'GET', params, data, headers, signal } = mergedConfig;

  // Build full URL with query params
  let fullUrl = `${baseURL}${url}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Handle arrays by adding multiple entries with the same key
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, String(item)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      fullUrl += `?${queryString}`;
    }
  }

  // Request interceptor: Add CSRF token
  const csrfToken = Cookies.get('csrftoken');
  const fetchHeaders = new Headers(headers);
  const hasExplicitMultipart =
    fetchHeaders.get('Content-Type')?.toLowerCase().startsWith('multipart/form-data') ?? false;
  if (hasExplicitMultipart) {
    fetchHeaders.delete('Content-Type');
  }

  if (csrfToken) {
    fetchHeaders.set('X-CSRFToken', csrfToken);
  }

  const isFormData =
    typeof FormData !== 'undefined' && data instanceof FormData;

  // Build request options
  const requestInit: RequestInit = {
    method,
    headers: fetchHeaders,
    credentials: 'include',
    signal: signal || controller.signal,
  };

  if (data !== undefined && data !== null) {
    if (isFormData) {
      requestInit.body = data as FormData;
    } else if (typeof data === 'string' || data instanceof Blob || data instanceof ArrayBuffer) {
      requestInit.body = data as BodyInit;
    } else {
      if (!fetchHeaders.has('Content-Type')) {
        fetchHeaders.set('Content-Type', 'application/json');
      }
      requestInit.body = JSON.stringify(data);
    }
  }

  // Make the request and return unwrapped data (like axios)
  const promise = fetch(fullUrl, requestInit)
    .then(async (response) => {
      // Response interceptor: Handle errors
      if (!response.ok) {
        console.error(`Fetch error: ${method} ${fullUrl} - Status: ${response.status}`);

        // Parse error data first
        let errorData: unknown;
        try {
          errorData = await response.clone().json();
        } catch {
          errorData = await response.clone().text();
        }

        const apiError = isApiErrorBody(errorData) ? errorData : undefined;

        // The user doesn't have a classroom (but avoid a redirect loop)
        if (apiError?.code === 'no_userclass' && window.location.pathname !== '/no-classroom') {
          window.location.href = '/no-classroom';
          throw new Error('No classroom assigned');
        }

        // The server logs the user out when a CSRF check fails
        if (apiError?.code === 'csrf_failed') {
          console.warn('CSRF token validation failed. Logging out...');
          performLogout();
          throw new Error('Session expired. Please log in again.');
        }

        throw new FetchError(apiError?.detail ?? response.statusText, response.status, errorData);
      }

      // Parse and return data directly (unwrapped, like axios)
      const contentType = response.headers.get('content-type');
      const rawText = await response.text();

      if (contentType && contentType.includes('application/json')) {
        if (!rawText) {
          return undefined;
        }

        try {
          return JSON.parse(rawText);
        } catch (error) {
          console.warn('Failed to parse JSON response, returning raw text.', {
            url,
            error,
            rawText,
          });
          return rawText as T;
        }
      }

      return rawText as T;
    })
    .catch((error) => {
      throw error;
    }) as CancellablePromise<T>;

  // Add cancel method
  promise.cancel = () => {
    controller.abort();
  };

  return promise;
};

export default customInstance;
